import { computed, signal, Component, inject, TemplateRef, Signal, viewChild } from '@angular/core'
import { PageHeaderComponent, PageHeaderMenuItem } from '../page-header/page-header.component'
import { PageContentComponent } from '../page-content/page-content.component'
import { FilterService } from 'src/app/shared/services/filter/filter.service'
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  from,
  NEVER,
  switchMap,
  take,
  tap,
} from 'rxjs'
import { ActivatedRoute, Router } from '@angular/router'
import { Location } from '@angular/common'
import { ActiveFilterListComponent } from '../table/filters/active-filter-list/active-filter-list.component'
import { MatMenuModule } from '@angular/material/menu'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { ColDef, GetRowIdFunc } from 'ag-grid-community'
import { ServicePointFilterModalComponent } from '../modals/filters/service-point-filter-modal.component'
import { DateCellComponent } from '../table/cells/date-cell.component'
import { ServicePointCellComponent } from '../table/cells/service_point_cell.component'
import { TableHeaderComponent } from '../table/headers/table-header.component'
import { DataTableComponent } from '../table/data-table.component'
import { TableSortService } from '../table/services/table-sort.service'
import { TablePaginationComponent } from '../table/table-pagination/table-pagination.component'
import { ActionCellComponent } from '../table/cells/action-cell.component'
import { DateRangeFilterModalComponent } from '../modals/filters/date-range-filter-modal.component'
import { dateOnlyFormat, dateTimeWithoutTimezone, est } from 'src/app/shared/utilities/date'
import { DateRangeFilter } from 'src/app/shared/services/filter/date-range-filter'
import { ServicePointGroupFilter } from 'src/app/shared/services/filter/service-point-group-filter'
import { ServicePointSearchFilter } from 'src/app/shared/services/filter/service-point-search-filter'
import { ServicePointFilter } from 'src/app/shared/services/filter/service-point-filter'
import { StringFilter } from 'src/app/shared/services/filter/string-filter'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { ToastService } from '../toasts/toasts.service'
import openModal from '../../shared/services/openModal'
import { isEqual } from 'lodash'
import { IntervalUsage } from 'src/app/shared/models/intervalUsage'
import { LabeledPropertyComponent } from '../labeled-property/labeled-property.component'
import { DateDisplayComponent } from '../date-display/date-display.component'
import { TableEditableDataService } from '../table/services/table-editable-data-service'
import { IntervalUsageService } from './interval-usage.service'
import { FilterParams, PaginationParams } from 'src/app/shared/utilities/http-params'
import { StringFilterModalComponent } from '../modals/filters/string-filter-modal.component'
import { IntervalUsageHistoryModalComponent } from '../modals/interval-usage-history-modal/interval-usage-history-modal.component'
import { UpdateIntervalUsageModalComponent } from '../modals/update-interval-usage-modal/update-interval-usage-modal.component'
import { ConfirmationModalComponent } from '../modals/confirmation-modal/confirmation-modal.component'
import { IntervalUsageImportModalComponent } from '../modals/interval-usage-import-modal/interval-usage-import.modal.component'
import { format } from 'date-fns'
import { NumberCellComponent } from '../table/cells/number-cell.component'

type DeleteTemplateRef = TemplateRef<{ ctx: IntervalUsage }>
type ExportTemplateRef = TemplateRef<{ ctx: {
  filterGroup: string,
  servicePointIdProvided: string | undefined
  marketIntervalProvided: string | undefined
} }>

@Component({
  imports: [
    MatMenuModule,
    PageHeaderComponent,
    PageContentComponent,
    LabeledPropertyComponent,
    ActiveFilterListComponent,
    DataTableComponent,
    TablePaginationComponent,
    DateDisplayComponent,
  ],
  providers: [TableSortService, TableEditableDataService],
  selector: 'app-interval-usage',
  styles: `
    .warn {
      color: goldenrod;
    }

    .error {
      color: red
    }
  `,
  template: `
    <div app-page-header header="Interval Usage" [menuItems]="pageHeaderMenuItems"></div>
    <app-page-content>
      <app-active-filter-list [filterGroup]="constructor.name" />
      <app-data-table [loading]="loading()" [data]="data()" [getRowId]="getRowId" [columnDefs]="columnDefs"></app-data-table>
      <app-table-pagination
        [pageNumber]="pageNumber()"
        [pageSize]="pageSize()"
        [totalResults]="totalResults()"
        (pageNumberChange)="pageNumber.set($event)"
        (pageSizeChange)="pageSize.set($event)"
      />
    </app-page-content>

    <ng-template #deleteConfirmation let-ctx="ctx">
      Are you sure you want to delete all interval usage for the following service point and operating day?
      @if (ctx) {
        <div app-labeled-property label="Service Point">{{ ctx.service_point_id }}</div>
        <div app-labeled-property label="Market Interval">
          <div app-date-display [value]="ctx.market_interval"></div>
        </div>
      }
    </ng-template>

    <ng-template #exportConfirmation let-ctx="ctx">
      @if (ctx) {        
        <div app-labeled-property label="Service Point" [class.error]="!ctx.servicePointIdProvided">{{ctx.servicePointIdProvided ? 'Provided' : 'Not Provided'}}</div>
        <div app-labeled-property label="Market Interval" [class.error]="!ctx.marketIntervalProvided">
          {{ctx.marketIntervalProvided ? 'Provided' : 'Not Provided'}}
        </div>
        <app-active-filter-list [filterGroup]="ctx.filterGroup"></app-active-filter-list>
        <span class="warn">
          If you are exporting a large data set, it could take some time. Please
          be patient and do not refresh the page until the data has been
          downloaded
        </span>
      }
    </ng-template>
  `,
})
export class IntervalUsageComponent {
  private modalService = inject(NgbModal)
  private filterService = inject(FilterService)
  private sortService = inject(TableSortService)
  private intervalUsageService = inject(IntervalUsageService)
  private toastService = inject(ToastService)
  private router = inject(Router)
  private route = inject(ActivatedRoute)
  private location = inject(Location)

  protected readonly dateOnlyFormat = dateOnlyFormat
  protected readonly refresh = signal(0)
  protected readonly loading = signal(false)
  protected readonly pageSize = signal(1000)
  protected readonly pageNumber = signal(1)
  protected readonly totalResults = signal(0)
  protected readonly data = signal<IntervalUsage[]>([])
  protected readonly getRowId: GetRowIdFunc<IntervalUsage> = (data) => data.data.interval_measurement_uid
  protected readonly paginationParams = computed<PaginationParams>(() => ({
    pageNumber: this.pageNumber(),
    pageSize: this.pageSize(),
  }))

  private deleteConfirmationTemplateRef: Signal<DeleteTemplateRef | undefined> =
    viewChild<TemplateRef<{ ctx: IntervalUsage }>>('deleteConfirmation')

  private exportConfirmationTemplateRef: Signal<ExportTemplateRef | undefined> =
    viewChild<TemplateRef<{ ctx: {
      filterGroup: string,
      servicePointIdProvided: string | undefined
      marketIntervalProvided: string | undefined
    } }>>('exportConfirmation')

  protected readonly pageHeaderMenuItems: readonly PageHeaderMenuItem[] = [
    { label: 'Export', onClick: () => this.openExportModal() },
    { label: 'Import', onClick: () => this.openImportModal() },
    { label: 'Create', onClick: () => this.openCreateModal() },
  ]

  protected readonly columnDefs: ColDef[] = [
    {
      filter: true,
      sortable: true,
      editable: false,
      flex: 1,
      minWidth: 100,
      width: 150,
      field: 'service_point_uid',
      headerName: 'Service Point',
      cellRenderer: ServicePointCellComponent,
      headerComponent: TableHeaderComponent,
      headerComponentParams: {
        filterGroup: IntervalUsageComponent.name,
        sortField: 'service_point_id',
        additionalFields: [
          'service_point_group_uid',
          'service_point_id_search',
        ],
        modal: ServicePointFilterModalComponent,
        modalOptions: { centered: true },
        modalInit: {
          partialField: 'service_point_id_search',
          groupField: 'service_point_group_uid',
        },
      },
    },
    {
      editable: false,
      minWidth: 100,
      width: 150,
      filter: true,
      sortable: true,
      headerName: 'Market Interval',
      field: 'market_interval',
      cellRenderer: DateCellComponent,
      cellRendererParams: {
        format: `${dateOnlyFormat} HH:00`,
        date: (d: IntervalUsage) => d.market_interval,
      },
      headerComponent: TableHeaderComponent,
      headerComponentParams: {
        filterGroup: IntervalUsageComponent.name,
        modal: DateRangeFilterModalComponent,
        modalOptions: { centered: true },
        modalInit: {},
      },
    },
    {
      filter: false,
      sortable: false,
      editable: false,
      minWidth: 100,
      width: 150,
      headerName: 'Usage (kW\u00A0h)',
      field: 'measurement_value',
      cellRenderer: NumberCellComponent,
      cellRendererParams: {
        num: (d: IntervalUsage) => d.measurement_value,
        precision: 8,
      },
      headerComponent: TableHeaderComponent,
      headerComponentParams: {
        filterGroup: IntervalUsageComponent.name,
      },
    },
    {
      filter: false,
      sortable: false,
      editable: false,
      minWidth: 100,
      width: 175,
      headerName: 'Last Updated At (STL)',
      field: 'last_update_time',
      cellRenderer: DateCellComponent,
      cellRendererParams: {
        format: dateTimeWithoutTimezone,
        date: (d: IntervalUsage) => d.last_update_time,
        viewAsTz: 'America/Chicago',
      },
      headerComponent: TableHeaderComponent,
      headerComponentParams: {
        filterGroup: IntervalUsageComponent.name,
      },
    },
    {
      editable: false,
      minWidth: 100,
      filter: true,
      sortable: true,
      width: 175,
      headerName: 'Last Updated By',
      field: 'last_updated_by',
      headerComponent: TableHeaderComponent,
      headerComponentParams: {
        filterGroup: IntervalUsageComponent.name,
        modal: StringFilterModalComponent,
        modalOptions: { centered: true },
        modalInit: {},
      },
    },
    {
      filter: false,
      sortable: false,
      editable: false,
      minWidth: 100,
      width: 75,
      pinned: 'right',
      resizable: false,
      suppressMovable: true,
      cellRenderer: ActionCellComponent<IntervalUsage>,
      cellRendererParams: {
        actions: [
          {
            name: 'Edit',
            onAction: (data: IntervalUsage) => this.openUpdateModal(data),
          },
          {
            name: 'View History',
            onAction: (data: IntervalUsage) => this.openHistoryModal(data),
          },
          {
            name: 'Delete Usage',
            onAction: (data: IntervalUsage) => this.openDeleteModal(data),
            sensitive: true,
          },
        ],
      },
      headerComponent: TableHeaderComponent,
      headerComponentParams: {
        filterGroup: IntervalUsageComponent.name,
      },
    },
  ]

  constructor() {
    const { dispatch } = this.filterService
    dispatch(FilterService.InitFiltersAction({
      fieldGroup: IntervalUsageComponent.name,
      fields: {
        service_point_uid: new ServicePointFilter({
          config: {
            allowMultiple: true,
            meta: {
              fieldGroup: IntervalUsageComponent.name,
              fieldName: 'service_point_uid',
              fieldDisplay: 'Service Point',
            },
          },
        }, dispatch),
        service_point_group_uid: new ServicePointGroupFilter({
          config: {
            allowMultiple: true,
            meta: {
              fieldGroup: IntervalUsageComponent.name,
              fieldName: 'service_point_group_uid',
              fieldDisplay: 'Service Point',
            },
          },
        }, dispatch),
        service_point_id_search: new ServicePointSearchFilter({
          config: {
            meta: {
              fieldGroup: IntervalUsageComponent.name,
              fieldName: 'service_point_id_search',
              fieldDisplay: 'Service Point',
            },
          },
        }, dispatch),
        market_interval: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: false,
            showSeconds: false,
            meta: {
              fieldGroup: IntervalUsageComponent.name,
              fieldName: 'market_interval',
              fieldDisplay: 'Market Interval',
            },
          },
        }, dispatch),
        last_updated_by: new StringFilter({
          config: {
            allowMultiple: true,
            meta: {
              fieldGroup: IntervalUsageComponent.name,
              fieldName: 'last_updated_by',
              fieldDisplay: 'Last Updated By',
            },
          },
        }, dispatch),
      },
    }))

    combineLatest([
      toObservable(this.paginationParams),
      this.filterService
        .asParams(IntervalUsageComponent.name)
        .pipe(
          tap(() => {
            if (this.pageNumber() !== 1) {
              this.pageNumber.set(1)
            }
          }),
          distinctUntilChanged(isEqual),
          filter((filters: FilterParams[]) => [
            ['service_point_uid', 'service_point_id_search', 'service_point_group_uid', 'cp_node_code'], 
            ['market_interval'],
          ].every(filterGroup => filterGroup.some(filterName => !!filters.find(f => f.name === filterName)))),
        ),
      this.sortService.getOrder(),
      toObservable(this.refresh),
    ])
      .pipe(
        takeUntilDestroyed(),
        tap(() => this.loading.set(true)),
        switchMap(([pagination, filters, order]) => this.intervalUsageService.getIntervalUsage(pagination, filters, order)),
        catchError(this.handleLoadingError('Something went wrong loading Interval Usage!')),
      )
      .subscribe({
        error: this.handleLoadingError('Something went wrong loading Interval Usage!'),
        next: (res) => {
          this.totalResults.set(res.total)
          this.data.set(res.results)
          this.loading.set(false)
        },
      })

    from(this.filterService.manageQueryParms(
      this.router,
      this.route,
      this.location,
      IntervalUsageComponent.name,
    ))
      .pipe(
        switchMap(obs$ => obs$),
        takeUntilDestroyed(),
      )
      .subscribe()
  }

  protected handleLoadingError(defaultMessage: string){
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (e: any) => {
      this.data.set([])
      this.loading.set(false)
      this.toastService.show({ type: 'error', message: e.detail ?? defaultMessage })
      return NEVER
    }
  }

  protected openHistoryModal(data: IntervalUsage) {
    openModal(
      this.modalService, 
      IntervalUsageHistoryModalComponent, 
      { centered: true, windowClass: 'confirmation-modal' },
    ).init(data)
  }

  protected openCreateModal() {
    openModal(
      this.modalService,
      UpdateIntervalUsageModalComponent,
      { centered: true, windowClass: 'confirmation-modal' },
    ).init({
      action: 'Create',
      onCancel: () => this.onCancel(),
      onUpdate: (data: Partial<IntervalUsage>[]) => this.onCreate(data),
    })
  }
  
  protected onCreate(data: Partial<IntervalUsage>[]) {
    this.intervalUsageService
      .createIntervalUsage(data)
      .subscribe({
        error: e => {
          const message = 'Something went wrong creating usage data' + (e.message ? `: ${e.message}` : '')
          this.toastService.show({ type: 'error', message })
        },
        next: () => {
          this.refresh.update(v => v + 1)
          this.toastService.show({ type: 'success', message: 'Create successful!' })
          this.modalService.dismissAll()
        },
      })
  }

  protected openUpdateModal(data: IntervalUsage) {
    openModal(
      this.modalService,
      UpdateIntervalUsageModalComponent,
      { centered: true, windowClass: 'confirmation-modal' },
    ).init({
      action: 'Update',
      onCancel: () => this.onCancel(),
      onUpdate: (data: Partial<IntervalUsage>[]) => this.onUpdate(data),
      data,
    })
  }

  protected onUpdate(data: Partial<IntervalUsage>[]) {
    this.intervalUsageService
      .createIntervalUsage(data)
      .subscribe({
        error: e => {
          const message = 'Something went wrong updating usage data' + (e.message ? `: ${e.message}` : '')
          this.toastService.show({ type: 'error', message })
        },
        next: () => {
          this.refresh.update(v => v + 1)
          this.toastService.show({ type: 'success', message: 'Update successful!' })
          this.modalService.dismissAll()
        },
      })
  }

  protected async openExportModal() {
    const state = await firstValueFrom(this.filterService.state(IntervalUsageComponent.name))
    const marketIntervalProvided = state['market_interval'].asCount() > 0
    const servicePointIdProvided = 
      state['service_point_uid']?.asCount() +
      state['service_point_id_search']?.asCount() +
      state['service_point_group_uid']?.asCount() > 0
    openModal(this.modalService, ConfirmationModalComponent, { centered: true }).init({
      headerText: 'Export',
      confirmButtonText: 'Download',
      bodyTemplate: this.exportConfirmationTemplateRef(),
      bodyContext: { 
        filterGroup: IntervalUsageComponent.name,
        servicePointIdProvided,
        marketIntervalProvided,
      },
      canConfirm: () =>  servicePointIdProvided && marketIntervalProvided,
      onCancel: () => this.onCancel(),
      onConfirm: () => this.onExport(),
    })
  }

  protected onExport() {
    this.filterService
      .asParams(IntervalUsageComponent.name)
      .pipe(
        take(1),
        switchMap((params) => this.intervalUsageService.downloadExports(params)),
      )
      .subscribe({
        error: e => {
          const message = 'Something went wrong exporting usage data' + (e.message ? `: ${e.message}` : '')
          this.toastService.show({ type: 'error', message })
        },
        next: () => {
          this.toastService.show({ type: 'success', message: 'Export successful!' })
          this.modalService.dismissAll()
        },
      })
  }

  protected openImportModal() {
    openModal(this.modalService, IntervalUsageImportModalComponent, { centered: true }).init({
      onImport: (data: Partial<IntervalUsage>[]) => this.onImport(data),
      onCancel: () => this.onCancel(),
    })
  }

  protected onImport(data: Partial<IntervalUsage>[]) {
    this.intervalUsageService
      .createIntervalUsage(data)
      .subscribe({
        error: e => {
          const message = 'Something went wrong importing usage data' + (e.message ? `: ${e.message}` : '')
          this.toastService.show({ type: 'error', message })
        },
        next: () => {
          this.refresh.update(v => v + 1)
          this.toastService.show({ type: 'success', message: 'Import successful!' })
          this.modalService.dismissAll()
        },
      })
  }

  protected openDeleteModal(data: IntervalUsage) {
    openModal(this.modalService, ConfirmationModalComponent, { centered: true }).init({
      headerText: 'Confirm Deletion',
      confirmButtonText: 'Delete',
      bodyTemplate: this.deleteConfirmationTemplateRef(),
      bodyContext: { ...data, market_interval: data.market_interval },
      onCancel: () => this.onCancel(),
      onConfirm: () => this.onDelete(data),
    })
  }

  protected onDelete (data: IntervalUsage) {
    this.intervalUsageService
      .delete(data.service_point_uid, format(est(data.market_interval), this.dateOnlyFormat))
      .subscribe({
        error: e => {
          const message = 'Something went wrong deleting usage data' + (e.message ? `: ${e.message}` : '')
          this.toastService.show({ type: 'error', message })
        },
        next: () => {
          this.refresh.update(v => v + 1)
          this.toastService.show({ type: 'success', message: 'Deletion successful!' })
          this.modalService.dismissAll()
        },
      })
  }

  protected onCancel() {
    this.modalService.dismissAll()
  }
}
